import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, orderCupsCount } from "@/lib/api";
import { useCanAdmin } from "@/lib/useCanAdmin";
import { ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import ColorBreakdownModal from "@/components/orders/ColorBreakdownModal";
import OrderPrintSheet, { OrderPrintCss } from "@/components/orders/OrderPrintSheet";
import ItemsEditor from "@/components/orders/ItemsEditor";
import EmitNfeButton from "@/components/orders/EmitNfeButton";
import OrderHeaderCard from "@/components/orders/OrderHeaderCard";
import OrderItemsCard from "@/components/orders/OrderItemsCard";
import ProductionSummaryCard from "@/components/orders/ProductionSummaryCard";
import CustomerDetailCard from "@/components/orders/CustomerDetailCard";
import ShippingCard from "@/components/orders/ShippingCard";
import ObservationsCard from "@/components/orders/ObservationsCard";
import FinancialCardMarketplace from "@/components/orders/FinancialCardMarketplace";
import DesignerNotesCard from "@/components/orders/DesignerNotesCard";
import OrderSummarySidebar from "@/components/orders/OrderSummarySidebar";
import MissingInfoModal from "@/components/orders/MissingInfoModal";
import { aggregateByType, missingProductionInfo } from "@/components/orders/orderDetailUtils";

export default function OrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canAdmin } = useCanAdmin();
  const [order, setOrder] = useState(null);
  const [silkColor, setSilkColor] = useState("");
  const [silkOptions, setSilkOptions] = useState([]);
  const [notes, setNotes] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingItems, setEditingItems] = useState(false);
  const [editingColors, setEditingColors] = useState(null); // {item, index}
  const [missingInfo, setMissingInfo] = useState(null); // [{sku, name, missing[]}]
  const [sentOk, setSentOk] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
    setSilkColor(data.silk_color || "");
    setNotes(data.notes || "");
    setImageUrl(data.image_url || "");
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get("/silk-colors").then(({ data }) => setSilkOptions(data));
  }, []);

  // Auto-scale da impressão: mede o conteúdo e aplica transform:scale para caber em 1 página A4.
  // Listeners são registrados uma única vez no mount (deps=[]) — funções internas não devem
  // recriar listeners a cada render.
  useEffect(() => {
    const beforePrint = () => {
      const sheet = document.getElementById("op-print-sheet");
      if (!sheet) return;
      sheet.style.transform = "";
      sheet.style.transformOrigin = "top left";
      const MAX_HEIGHT_PX = 277 * 3.7795;
      const contentHeight = sheet.scrollHeight;
      if (contentHeight > MAX_HEIGHT_PX && contentHeight > 0) {
        const scale = Math.max(0.55, MAX_HEIGHT_PX / contentHeight);
        sheet.style.transform = `scale(${scale})`;
        sheet.style.width = `${100 / scale}%`;
      }
    };
    const afterPrint = () => {
      const sheet = document.getElementById("op-print-sheet");
      if (sheet) {
        sheet.style.transform = "";
        sheet.style.width = "";
      }
    };
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    return () => {
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, []);

  if (!order) return <div className="p-12">Carregando…</div>;

  const uploadImage = async (file) => {
    if (file.size > 10 * 1024 * 1024) {
      alert("Imagem muito grande (máx 10MB). Reduza antes de enviar.");
      return null;
    }
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/uploads/order-image", fd);
    return data.url; // URL relativa /api/files/... — o binário fica no Object Store
  };

  const totalCups = orderCupsCount(order);
  const productionSummary = aggregateByType(order.order_items);

  const save = async (sendToProduction = false, doPrint = false, confirmClearDetails = false) => {
    setSaving(true);
    try {
      const auto_items = (order.order_items || []).map((it) => ({
        sku: it.sku, color: it.type || "Genérico", qty: it.cups || 0, units_per_unit: it.units_per_unit || 1,
      }));
      await api.put(`/orders/${id}/production`, {
        items: auto_items,
        silk_color: silkColor || null,
        image_url: imageUrl || null,
        notes: notes || null,
        send_to_production: sendToProduction,
        confirm_clear_details: confirmClearDetails,
      });
      await load();
      if (doPrint) setTimeout(() => window.print(), 400);
      return true;
    } catch (e) {
      // 409 = pedido já tinha detalhes preenchidos e o payload zeraria tudo.
      // Pede confirmação explícita antes de continuar (o backend só apaga
      // quando `confirm_clear_details: true`).
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "would_clear_details") {
        const existing = detail.existing || {};
        const msg =
          `Este pedido já tem detalhes de desenvolvimento salvos:\n` +
          `  • Itens de produção: ${existing.production_items_count || 0}\n` +
          `  • Silk: ${existing.silk_color || "(sem)"}\n` +
          `  • Imagem: ${existing.image_url_present ? "sim" : "não"}\n\n` +
          `Se você salvar agora, TUDO isso será apagado.\n\nDeseja mesmo apagar e salvar em branco?`;
        if (window.confirm(msg)) {
          setSaving(false);
          return save(sendToProduction, doPrint, true);
        }
      } else {
        alert(detail?.message || detail || "Erro ao salvar");
      }
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Envio para produção: valida o checklist por item antes de enviar.
  const confirmSend = async () => {
    setMissingInfo(null);
    const ok = await save(true, false);
    if (!ok) return;
    setSentOk(true);
    setTimeout(() => navigate("/orders"), 1500);
  };

  const handleSendToProduction = () => {
    const missing = missingProductionInfo(order, imageUrl, silkColor);
    if (missing.length === 0) {
      confirmSend();
      return;
    }
    setMissingInfo(missing);
  };

  // Atualiza silk_color de um item individual sem abrir o modal de edição
  const updateItemSilk = async (index, value) => {
    const items = (order.order_items || []).map((it, i) => ({
      sku: it.sku,
      name: it.name,
      qty: it.qty,
      unit_price: it.unit_price,
      silk_color: i === index ? (value || null) : (it.silk_color || null),
      image_url: it.image_url || null,
      production_notes: it.production_notes || null,
      color_breakdown: it.color_breakdown || [],
    }));
    try {
      const { data } = await api.put(`/orders/${id}/items`, { items, keep_net_value: true });
      setOrder(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao salvar cor do silk");
    }
  };

  // Atualiza image_url ou production_notes de um item individual
  const updateItemArt = async (index, field, value) => {
    const items = (order.order_items || []).map((it, i) => ({
      sku: it.sku,
      name: it.name,
      qty: it.qty,
      unit_price: it.unit_price,
      silk_color: it.silk_color || null,
      image_url: i === index && field === "image_url" ? (value || null) : (it.image_url || null),
      production_notes: i === index && field === "production_notes" ? (value || null) : (it.production_notes || null),
      color_breakdown: it.color_breakdown || [],
    }));
    try {
      const { data } = await api.put(`/orders/${id}/items`, { items, keep_net_value: true });
      setOrder(data);
    } catch (e) {
      alert(e?.response?.data?.detail || "Erro ao salvar arte do item");
    }
  };

  // Upload de imagem por item (Object Store — só a URL vai para o banco)
  const onItemImageFile = async (index, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const url = await uploadImage(file);
      if (url) updateItemArt(index, "image_url", url);
    } catch {
      alert("Falha ao enviar a imagem. Tente novamente.");
    }
  };

  return (
    <div className="p-6 md:p-10 print:p-0">
      <div className="flex items-center justify-between mb-6 print:hidden">
        <button onClick={() => navigate("/orders")} className="flex items-center gap-2 text-sm hover:underline" data-testid="back-button">
          <ArrowLeft size={16} /> Voltar para Pedidos
        </button>
        <EmitNfeButton order={order} />
      </div>

      <div className="print:hidden">
        <OrderHeaderCard order={order} onReload={load} />

        {/* MAIN GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <div className="lg:col-span-8 space-y-5">
            <OrderItemsCard
              order={order}
              silkOptions={silkOptions}
              canAdmin={canAdmin}
              totalCups={totalCups}
              onEditItems={() => setEditingItems(true)}
              onEditColors={(item, index) => setEditingColors({ item, index })}
              onUpdateSilk={updateItemSilk}
              onUpdateArt={updateItemArt}
              onItemImageFile={onItemImageFile}
            />

            <ProductionSummaryCard summary={productionSummary} />

            <CustomerDetailCard order={order} />

            {/* Financial + shipping */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {canAdmin ? (
                <FinancialCardMarketplace order={order} />
              ) : (
                <div className="border border-dashed border-zinc-300 bg-white p-6 text-center text-zinc-400 text-xs" data-testid="financial-hidden">
                  Dados financeiros restritos à administração.
                </div>
              )}
              <ShippingCard order={order} />
            </div>

            <ObservationsCard order={order} />

            <DesignerNotesCard notes={notes} setNotes={setNotes} />
          </div>

          {/* RIGHT sidebar */}
          <div className="lg:col-span-4">
            <OrderSummarySidebar
              order={order}
              totalCups={totalCups}
              canAdmin={canAdmin}
              saving={saving}
              onSave={save}
              onSend={handleSendToProduction}
            />
          </div>
        </div>
      </div>

      {/* === PRINT VIEW === */}
      <div className="hidden print:block">
        <OrderPrintSheet
          order={order}
          notes={notes}
          imageUrl={imageUrl}
          productionSummary={productionSummary}
          totalCups={totalCups}
          sheetId="op-print-sheet"
        />
      </div>

      <OrderPrintCss />

      {editingItems && <ItemsEditor order={order} silkOptions={silkOptions} onSaved={(o) => { setOrder(o); setEditingItems(false); }} onClose={() => setEditingItems(false)} />}
      {missingInfo && (
        <MissingInfoModal
          missing={missingInfo}
          saving={saving}
          onBack={() => setMissingInfo(null)}
          onContinue={confirmSend}
        />
      )}
      {sentOk && (
        <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-6" data-testid="send-success-overlay">
          <div className="bg-white border-2 border-black px-8 py-10 text-center max-w-md w-full">
            <CheckCircle size={56} weight="fill" color="#00A300" className="mx-auto mb-4" />
            <div className="font-head text-xl font-black tracking-tight" data-testid="send-success-message">
              Sucesso este pedido foi enviado para a Produção
            </div>
          </div>
        </div>
      )}
      {editingColors && (
        <ColorBreakdownModal
          orderId={order.id}
          item={editingColors.item}
          itemIndex={editingColors.index}
          allItems={order.order_items}
          onSaved={(o) => { setOrder(o); setEditingColors(null); }}
          onClose={() => setEditingColors(null)}
        />
      )}
    </div>
  );
}
